import { firstValueFrom } from 'rxjs';

/**
 * The Client ABC groups of an integration, as the widget dialogs need them: key, title and owner.
 *
 * OE-265: both widget dialogs downloaded every group of the integration on every open — 312
 * documents, 2.4 MB with their summaries and account lists on dev — and the page froze ~3.8 s while
 * Firestore converted them, to fill one dropdown of titles. This keeps the three fields per
 * integration and downloads again only once the list is a minute old (a group made on the Client
 * ABC page a moment ago still appears).
 */
export interface ClientAbcGroupRef {
    key: string;
    title: string;
    userKey: string;
}

const FRESH_FOR_MS = 60_000;
const cache = new Map<string, { at: number; refs: Promise<ClientAbcGroupRef[]> }>();

/** `db` is the compat AngularFirestore (typed loosely so specs can pass a stub chain). */
export function clientAbcGroupRefs(
    db: any,
    config: { type: string; key: string },
    now: number = Date.now()
): Promise<ClientAbcGroupRef[]> {
    const id = `${config.type}/${config.key}`;
    const hit = cache.get(id);
    if (hit && now - hit.at < FRESH_FOR_MS) return hit.refs;

    const refs = firstValueFrom(
        db
            .collection('gateway')
            .doc(`--${config.type}--`)
            .collection('public')
            .doc(config.key)
            .collection('custom-client-abc-groups')
            .get()
    ).then((snapshot: any) =>
        (snapshot.docs as { data: () => any }[]).map((d) => {
            const { key, title, userKey } = d.data() || {};
            return { key, title, userKey } as ClientAbcGroupRef;
        })
    );
    // A failed download is not remembered.
    refs.catch(() => cache.delete(id));
    cache.set(id, { at: now, refs });
    return refs;
}

/** For specs. */
export function forgetClientAbcGroupRefs(): void {
    cache.clear();
}
